import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { enviarJson, pedirJson } from "./api";

/* Assistência por IA no chamado interno. O contrato é o de `api/app/dominios/assistencia_ia/`.

   O assistente SUGERE: o rascunho de resposta vai para o campo para a pessoa editar (nunca é
   enviado daqui), e a classificação é só exibida. Com o gateway desligado — o estado da produção
   hoje — `GET /assistencia-ia/estado` diz isso e a tela desliga os botões, sem erro. */

export const CHAVE_DA_ASSISTENCIA = ["assistencia-ia"] as const;

const esquemaEstado = z.object({ disponivel: z.boolean(), motivo: z.string().nullable() });
const esquemaResumo = z.object({ texto: z.string() });
const esquemaSugestao = z.object({
  texto: z.string(),
  artigos: z.array(z.object({ id: z.string().uuid(), titulo: z.string() })),
});
const esquemaAnalise = z.object({
  sentimento: z.enum(["positivo", "neutro", "negativo"]),
  urgencia_sugerida: z.enum(["baixa", "media", "alta", "urgente"]),
  urgencia_atual: z.string(),
  justificativa: z.string(),
  origem: z.string(),
});

export type EstadoDaAssistencia = z.infer<typeof esquemaEstado>;
export type SugestaoDeResposta = z.infer<typeof esquemaSugestao>;
export type AnaliseDaMensagem = z.infer<typeof esquemaAnalise>;

export const ROTULO_DO_SENTIMENTO: Record<AnaliseDaMensagem["sentimento"], string> = {
  positivo: "Positivo",
  neutro: "Neutro",
  negativo: "Negativo",
};

export const ROTULO_DA_URGENCIA_SUGERIDA: Record<AnaliseDaMensagem["urgencia_sugerida"], string> = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
  urgente: "Urgente",
};

export const ROTULO_DA_ORIGEM: Record<string, string> = {
  ultima_mensagem_do_cliente: "a última mensagem do cliente",
  descricao_do_chamado: "a descrição do chamado (o cliente ainda não escreveu depois)",
};

/**
 * O rascunho entra DEPOIS do que a pessoa já digitou, separado por uma linha em branco — nunca
 * por cima: apagar o texto de quem escreveu para colocar o da IA seria o pior jeito de ajudar.
 */
export function juntarRascunho(atual: string, rascunho: string): string {
  return atual.trim() ? `${atual.replace(/\s+$/, "")}\n\n${rascunho}` : rascunho;
}

export function useEstadoDaAssistencia(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DA_ASSISTENCIA, "estado"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/assistencia-ia/estado", esquemaEstado, { signal }),
  });
}

function caminho(ticketId: string, funcao: string) {
  return `/assistencia-ia/chamados/${encodeURIComponent(ticketId)}/${funcao}`;
}

export function useResumirConversa(ticketId: string) {
  return useMutation({
    mutationFn: () => enviarJson(caminho(ticketId, "resumo"), esquemaResumo, undefined),
  });
}

export function useSugerirResposta(ticketId: string) {
  return useMutation({
    mutationFn: () =>
      enviarJson(caminho(ticketId, "sugestao-de-resposta"), esquemaSugestao, undefined),
  });
}

export function useAnalisarMensagem(ticketId: string) {
  return useMutation({
    mutationFn: () => enviarJson(caminho(ticketId, "analise"), esquemaAnalise, undefined),
  });
}
